import {
  convertCmsToInches,
  convertInchesToCms,
  convertKgsToLbs,
  convertLbsToKgs,
  convertFeetToCm,
  convertCmToFeetInch,
  convertInToFt,
  calculateAmericanBmi,
  calculateMetricBmi,
  getAgeGroup,
} from '@/utils/lm_utilities';
import { formatCurrency } from './formatLocale';
import _round from 'lodash/round';
import _ceil from 'lodash/ceil';
import _floor from 'lodash/floor';

// const getSliderBounds = (unit, CheckMode, ageInMonths, dataResFloor, dataResCeil) => {
//   try {
//     const floorChart = dataResFloor?.charts?.[0];
//     const ceilChart = dataResCeil?.charts?.[0];

//     if (!floorChart || !ceilChart) return { min: 0, max: 100 };

//     const floorAge = floorChart.age;
//     const ceilAge = ceilChart.age;

//     if (floorAge == null || ceilAge == null || floorAge === ceilAge) {
//       return { min: 0, max: 100 };
//     }

//     const ratio = (ageInMonths - floorAge) / (ceilAge - floorAge); // Interpolation ratio (0 to 1)

//     const interpolate = (key) => {
//       const floorVal = floorChart[key] ?? null;
//       const ceilVal = ceilChart[key] ?? null;

//       if (floorVal == null && ceilVal == null) return null;
//       if (floorVal == null) return ceilVal;
//       if (ceilVal == null) return floorVal;

//       return floorVal + (ceilVal - floorVal) * ratio;
//     };

//     const p3 = interpolate('p_3') ?? interpolate('p_5') ?? interpolate('p_10');
//     const p97 = interpolate('p_97') ?? interpolate('p_95') ?? interpolate('p_90');
//     const p50 = interpolate('p_50') ?? interpolate('median');

//     if (p3 == null || p97 == null || p50 == null) return { min: 0, max: 100 };

//     // Add 5% dynamic buffer
//     const range = p97 - p3;
//     const margin = range * 0.25;

//     const safeMin = p3 - margin;
//     const safeMax = p97 + margin;

//     // Unit conversion
//     const convert = (val) => {
//       switch (unit) {
//         case 'lbs':
//           return +(val * 2.20462).toFixed(0);
//         case 'inches':
//           return +(val / 2.54).toFixed(0);
//         case 'feet':
//           return +(val / 30.48).toFixed(0);
//         case 'inches-lbs':
//           switch (CheckMode) {
//             case 'weight':
//               return +(val * 2.20462).toFixed(0);
//             case 'height':
//               return +(val / 2.54).toFixed(0);
//           }
//         case 'cms-kgs':
//         case 'kgs':
//         case 'cm':
//         case 'm':
//         default:
//           return +val.toFixed(0);
//       }
//     };

//     return {
//       min: _floor(convert(safeMin)),
//       max: _ceil(convert(safeMax)),
//     };
//   } catch (e) {
//     console.log('CHECK ERROR', e, 'CHECK ERROR');
//   }
// };
const getSliderBounds = (unit, CheckMode, ageInYears, dataResFloor, dataResCeil) => {
  try {
    const floorChart = dataResFloor?.charts?.[0];
    const ceilChart = dataResCeil?.charts?.[0];

    if (!floorChart || !ceilChart) return { min: 0, max: 100 };

    const floorAge = floorChart.age; // in years
    const ceilAge = ceilChart.age; // in years

    if (floorAge == null || ceilAge == null || floorAge === ceilAge) {
      return { min: 0, max: 100 };
    }

    // Clamp ratio between 0 and 1 to avoid overshoot
    const ratio = Math.min(Math.max((ageInYears - floorAge) / (ceilAge - floorAge), 0), 1);

    const interpolate = (key) => {
      const floorVal = floorChart[key] ?? null;
      const ceilVal = ceilChart[key] ?? null;

      if (floorVal == null && ceilVal == null) return null;
      if (floorVal == null) return ceilVal;
      if (ceilVal == null) return floorVal;

      return floorVal + (ceilVal - floorVal) * ratio;
    };

    const p3 = interpolate('p_2') ?? interpolate('p_3') ?? interpolate('p_5');
    const p97 = interpolate('p_99') ?? interpolate('p_98') ?? interpolate('p_97');
    const p50 = interpolate('p_50') ?? interpolate('median');

    if (p3 == null || p97 == null || p50 == null) return { min: 0, max: 100 };

    // Use smaller margin (5%) for net worth, not 25%
    const range = p97 - p3;
    const margin = range * 0.05;

    let safeMin = p3 - margin;
    let safeMax = p97 + margin;

    // In net worth, no negative min values
    if (unit === 'USD') {
      safeMin = Math.max(0, safeMin);
    }

    // Unit conversion / rounding
    const convert = (val) => {
      switch (unit) {
        case 'USD':
          return +val.toFixed(0);
        default:
          return +val.toFixed(0);
      }
    };

    return {
      min: _floor(convert(safeMin)),
      max: _ceil(convert(safeMax)),
    };
  } catch (e) {
    console.log('CHECK ERROR', e);
    return { min: 0, max: 100 };
  }
};

const lengthConfig = {
  stepValue: (unit) => {
    if (unit === 'inches' || unit === 'cms' || unit === 'kgs') return 0.5;
    if (unit === 'feet') return 1;
    if (unit === 'lbs') return 1;
  },
  min: (unit) => {
    if (unit === 'inches') return 12;
    if (unit === 'cms') return 30;
    if (unit === 'feet') return 1;
    if (unit === 'kgs') return 0.5;
    if (unit === 'lbs') return 1;
  },
  maxValue: (unit) => {
    if (unit === 'inches') return 40;
    if (unit === 'cms') return 100;
    if (unit === 'feet') return 4;
    if (unit === 'kgs') return 20;
    if (unit === 'lbs') return 50;
  },
};

export const heightConfig = {
  chartType: 'LengthChart',
  measurementType: 'height',
  chartTitle: 'Height',
  chartSubtitle: 'Height for Age',
  measurementConfig: {
    stepValue: (unit, isLengthChart) => {
      if (isLengthChart) {
        return lengthConfig.stepValue(unit);
      }
      if (unit === 'inches') return 0.5;
      if (unit === 'cms') return 1;
      if (unit === 'feet') return 1;
    },
    // min: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (unit === 'inches') return 30;
    //   if (unit === 'cms') return 75;
    //   if (unit === 'feet') return 4;
    // },
    min: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.min(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.min;
    },
    // min: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'inches') return 20;
    //     if (unit === 'cms') return 50;
    //     if (unit === 'feet') return 2;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'inches') return 30;
    //     if (unit === 'cms') return 80;
    //     if (unit === 'feet') return 3;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'inches') return 50;
    //     if (unit === 'cms') return 125;
    //     if (unit === 'feet') return 4;
    //   }
    // },
    // maxValue: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (unit === 'inches') return 100;
    //   if (unit === 'cms') return 250;
    //   if (unit === 'feet') return 8;
    // },
    maxValue: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.maxValue(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.max;
    },
    // maxValue: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'inches') return 40;
    //     if (unit === 'cms') return 100;
    //     if (unit === 'feet') return 3;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'inches') return 60;
    //     if (unit === 'cms') return 160;
    //     if (unit === 'feet') return 5;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'inches') return 80;
    //     if (unit === 'cms') return 200;
    //     if (unit === 'feet') return 7;
    //   }
    // },
    isSingleInput: (unit) => unit === 'inches' || unit === 'cms',
    getUnitDisplay: (formData) => {
      let value = formData.l_measurement;
      let value2;
      let unit2;
      let unit = 'inches';
      if (formData.unit === 'cms') {
        unit = 'cms';
      }
      if (formData.unit === 'feet') {
        value = formData.l_height_ft;
        unit = 'feet';
        value2 = formData.l_height_in;
        unit2 = 'inches';
      }
      return (
        <>
          (<span className="h_value">{value}</span> <strong>{unit}</strong>{' '}
          {value2 && <span className="h_value">{value2}</span>} {unit2 && <strong>{unit2}</strong>})
        </>
      );
    },
    getResultDisplay: ({ unit, l_height_ft, l_height_in, l_measurement }) => {
      return (
        <div className="d-flex justify-content-between my-md-2">
          <h6>Height</h6>
          <span>
            {unit === 'cms'
              ? l_measurement
              : unit === 'feet'
              ? l_measurement
              : _round(convertInchesToCms(l_measurement))}{' '}
            cms / {unit === 'inches' ? l_measurement : _round(convertCmsToInches(l_measurement))} inches
          </span>
        </div>
      );
    },
  },
  calculateMeasurement: (formData, originalValue, isInitial, urlUnit) => {
    let converted = {};
    const { unit } = formData;
    if (isInitial) {
      converted = {
        ...formData,
        l_measurement: _round(originalValue),
        in_incs: _round(convertCmsToInches(originalValue)),
        in_cms: _round(originalValue),
      };
      if (urlUnit && urlUnit !== 'cms') {
        converted = {
          ...heightConfig.handleMeasurement(originalValue, urlUnit, 'cms', null, converted),
          unit: urlUnit,
        };
      } else {
        // get unit by geo location
      }
    } else {
      if (unit === 'feet') {
        converted = {
          l_measurement: convertFeetToCm(formData.l_height_ft, formData.l_height_in),
        };
      } else if (unit === 'inches') {
        converted = {
          l_measurement: convertInchesToCms(formData.l_measurement),
        };
      } else {
        converted = {
          l_measurement: formData.l_measurement,
        };
      }
    }
    return converted;
  },
  handleMeasurement: (value, unit, previousUnit, _, formData) => {
    let converted = {};
    if (previousUnit === 'feet') {
      if (unit === 'inches') {
        converted = {
          l_measurement: _round(convertCmsToInches(convertFeetToCm(formData.l_height_ft, formData.l_height_in))),
          in_incs: _round(convertCmsToInches(convertFeetToCm(formData.l_height_ft, formData.l_height_in))),
          in_cms: _round(convertFeetToCm(formData.l_height_ft, formData.l_height_in)),
        };
      } else if (unit === 'cms') {
        converted = {
          l_measurement: _round(convertFeetToCm(formData.l_height_ft, formData.l_height_in)),
          in_cms: _round(convertFeetToCm(formData.l_height_ft, formData.l_height_in)),
          in_incs: convertCmsToInches(convertFeetToCm(formData.l_height_ft, formData.l_height_in)),
        };
      }
    } else {
      if (unit === 'inches') {
        converted = {
          l_measurement: _round(convertCmsToInches(value)),
          in_incs: _round(convertCmsToInches(value)),
          in_cms: _round(value),
        };
      } else if (unit === 'cms') {
        converted = {
          l_measurement: _round(convertInchesToCms(value)),
          in_cms: _round(convertInchesToCms(value)),
          in_incs: _round(value),
        };
      } else if (unit === 'feet') {
        let tempLength = value;
        let feet_inch;
        if (previousUnit === 'cms') {
          tempLength = value;
          feet_inch = convertCmToFeetInch(tempLength);
        }
        if (previousUnit === 'inches') {
          tempLength = convertInchesToCms(value);
          feet_inch = convertInToFt(value);
        }
        converted = {
          l_measurement: _round(tempLength),
          l_height_ft: feet_inch.feet,
          l_height_in: feet_inch.inch,
        };
      }
    }

    return converted;
  },
  unitOptions: {
    first: {
      name: 'cms',
      label: 'cms',
    },
    second: {
      name: 'inches',
      label: 'inches',
    },
    third: {
      name: 'feet',
      label: 'feet & inches',
    },
  },
};

export const weightConfig = {
  chartType: 'WeightChart',
  measurementType: 'weight',
  chartTitle: 'Weight',
  chartSubtitle: 'Weight for Age',
  measurementConfig: {
    stepValue: (unit, isLengthChart) => {
      if (isLengthChart) {
        return lengthConfig.stepValue(unit);
      }
      if (unit === 'kgs') return 0.5;
      if (unit === 'lbs') return 1;
    },
    // min: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (unit === 'kgs') return 2;
    //   else if (unit === 'lbs') return 4;
    //   else return 1;
    // },
    min: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.min(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.min;
    },
    // min: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 2.5;
    //     else if (unit === 'lbs') return 5.5;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 10;
    //     else if (unit === 'lbs') return 22;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 30;
    //     else if (unit === 'lbs') return 66;
    //   }
    // },

    // maxValue: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (unit === 'kgs') return 200;
    //   else if (unit === 'lbs') return 440;
    //   else return 200;
    // },
    maxValue: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.maxValue(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.max;
    },
    // maxValue: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 15;
    //     else if (unit === 'lbs') return 33;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 50;
    //     else if (unit === 'lbs') return 110;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 200;
    //     else if (unit === 'lbs') return 440;
    //   }
    // },

    isSingleInput: (unit) => true,
    getUnitDisplay: (formData) => {
      let value = formData.l_measurement;
      let unit = 'kgs';
      if (formData.unit === 'lbs') {
        unit = 'lbs';
      }
      return (
        <>
          (<span className="h_value">{value}</span> <strong>{unit}</strong>)
        </>
      );
    },
    getResultDisplay: ({ in_kgs, in_lbs, unit, l_measurement }) => (
      <div className="d-flex justify-content-between my-md-2">
        <h6>Weight</h6>
        <span>
          {unit === 'kgs' ? l_measurement : _round(convertLbsToKgs(l_measurement))} kgs /{' '}
          {unit === 'lbs' ? l_measurement : _round(convertKgsToLbs(l_measurement))} lbs
        </span>
      </div>
    ),
  },
  calculateMeasurement: (formData, originalValue, isInitial, urlUnit) => {
    let converted = {};
    const { unit } = formData;
    if (isInitial) {
      converted = {
        l_measurement: _round(convertKgsToLbs(originalValue)),
        in_lbs: _round(convertKgsToLbs(originalValue)),
        in_kgs: _round(originalValue),
      };
      if (urlUnit && urlUnit !== 'lbs') {
        converted = {
          ...weightConfig.handleMeasurement(converted.l_measurement, urlUnit),
          unit: urlUnit,
        };
      } else {
        // get unit by geo location
      }
    } else {
      if (unit === 'lbs') {
        converted = {
          l_measurement: convertLbsToKgs(formData.l_measurement),
        };
      } else {
        converted = {
          l_measurement: formData.l_measurement,
        };
      }
    }
    return converted;
  },
  handleMeasurement: (value, unit, _, isInitial) => {
    let converted = {};

    if (unit === 'lbs') {
      converted = {
        l_measurement: _round(convertKgsToLbs(value)),
        in_lbs: _round(convertKgsToLbs(value)),
        in_kgs: _round(value),
      };
    } else if (unit === 'kgs') {
      converted = {
        l_measurement: _round(convertLbsToKgs(value)),
        in_kgs: _round(convertLbsToKgs(value)),
        in_lbs: _round(value),
      };
    }

    return converted;
  },
  unitOptions: {
    first: {
      name: 'kgs',
      label: 'kgs',
    },
    second: {
      name: 'lbs',
      label: 'lbs',
    },
  },
};

export const headConfig = {
  chartType: 'HeadChart',
  measurementType: 'head',
  chartTitle: 'Head Circumference',
  chartSubtitle: 'Head Circumference for Age',
  measurementConfig: {
    stepValue: (unit) => {
      if (unit === 'cms') return 0.5;
      else if (unit === 'inches') return 0.2;
      else return 0.1;
    },
    // min: (unit) => {
    //   if (unit === 'cms') return 25;
    //   else if (unit === 'inches') return 10;
    //   else return 1;
    // },
    min: (unit, ageGroup) => {
      if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
        if (unit === 'cms') return 25; //33
        else if (unit === 'inches') return 10; //10
        else return 1;
      }
    },
    // maxValue: (unit) => {
    //   if (unit === 'cms') return 55;
    //   else if (unit === 'inches') return 25;
    //   else return 200;
    // },
    maxValue: (unit, ageGroup) => {
      if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
        if (unit === 'cms') return 49;
        else if (unit === 'inches') return 19;
        else return 25; //previous max
      }
    },
    isSingleInput: (unit) => true,
    getUnitDisplay: (formData) => {
      let value = formData.l_measurement;
      let unit = 'cms';
      if (formData.unit === 'inches') {
        unit = 'inches';
      }
      return (
        <>
          (<span className="h_value">{value}</span> <strong>{unit}</strong>)
        </>
      );
    },
    getResultDisplay: ({ unit, l_measurement }) => (
      <div className="d-flex justify-content-between my-md-2">
        <h6>Head Size</h6>
        <span>
          {unit === 'cms' ? l_measurement : _round(convertInchesToCms(l_measurement))} cms /{' '}
          {unit === 'inches' ? l_measurement : _round(convertCmsToInches(l_measurement))} inches
        </span>
      </div>
    ),
  },
  calculateMeasurement: (formData, originalValue, isInitial, urlUnit) => {
    let converted = {};
    const { unit } = formData;
    if (isInitial) {
      converted = {
        l_measurement: _round(originalValue),
        in_cms: _round(originalValue),
        in_incs: _round(convertCmsToInches(originalValue)),
      };

      if (urlUnit && urlUnit !== 'cms') {
        converted = {
          ...headConfig.handleMeasurement(converted.l_measurement, urlUnit),
          unit: urlUnit,
        };
      } else {
        // get unit by geo location
      }
    } else {
      if (unit === 'inches') {
        converted = {
          l_measurement: convertInchesToCms(formData.l_measurement),
        };
      } else {
        converted = {
          l_measurement: formData.l_measurement,
        };
      }
    }
    return converted;
  },
  handleMeasurement: (value, unit) => {
    let converted = {};
    if (unit === 'inches') {
      converted = {
        l_measurement: _round(convertCmsToInches(value)),
        in_incs: _round(convertCmsToInches(value)),
        in_cms: _round(value),
      };
    } else if (unit === 'cms') {
      converted = {
        l_measurement: _round(convertInchesToCms(value)),
        in_cms: _round(convertInchesToCms(value)),
        in_incs: _round(value),
      };
    }
    return converted;
  },
  unitOptions: {
    first: {
      name: 'cms',
      label: 'cms',
    },
    second: {
      name: 'inches',
      label: 'inches',
    },
  },
};

export const bmiConfig = {
  chartType: 'BmiChart',
  measurementType: 'bmi',
  chartTitle: 'BMI',
  chartSubtitle: 'BMI for Age',
  measurementConfig: {
    // min: (unit, mode, isLengthChart) => {
    //   if (isLengthChart) {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 0.5;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 1;

    //     if (unit === 'cms-kgs' && !mode) return 40;
    //     if (unit === 'inches-lbs' && !mode) return 15;
    //   }

    //   if (unit === 'cms-kgs' && mode === 'weight') return 2;
    //   if (unit === 'inches-lbs' && mode === 'weight') return 4;

    //   if (unit === 'cms-kgs' && !mode) return 75;
    //   if (unit === 'inches-lbs' && !mode) return 30;
    //   return 1;
    // },
    min: (unit, mode, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        if (unit === 'cms-kgs' && mode === 'weight') return 0.5;
        if (unit === 'inches-lbs' && mode === 'weight') return 1;

        if (unit === 'cms-kgs' && !mode) return 40;
        if (unit === 'inches-lbs' && !mode) return 15;
      }
      const checkMode = mode === 'weight' ? 'weight' : 'height';
      const bounds = getSliderBounds(unit, checkMode, ageGroup, dataResFloor, dataResCeil);
      return bounds.min;
    },
    // min: (unit, mode, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 0.5;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 1;
    //     if (unit === 'cms-kgs' && !mode) return 30;
    //     if (unit === 'inches-lbs' && !mode) return 12;
    //   }
    //   if (getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 2.5;
    //     if (unit === 'cms-kgs' && !mode) return 50;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 5.5;
    //     if (unit === 'inches-lbs' && !mode) return 20;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 10;
    //     if (unit === 'cms-kgs' && !mode) return 80;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 22;
    //     if (unit === 'inches-lbs' && !mode) return 30;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 30;
    //     if (unit === 'cms-kgs' && !mode) return 125;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 66;
    //     if (unit === 'inches-lbs' && !mode) return 50;
    //   }
    // },
    stepValue: (unit, mode, isLengthChart) => {
      if (isLengthChart) {
        if (mode === 'weight' && unit === 'inches-lbs') return 1;
        return 0.5;
      }

      if (mode === 'weight' && unit === 'inches-lbs') return 1;
      if (unit === 'inches-lbs' || unit === 'cms-kgs') return 0.5;
    },
    // maxValue: (unit, mode, isLengthChart) => {
    //   if (isLengthChart) {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 20;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 50;

    //     if (unit === 'cms-kgs' && !mode) return 100;
    //     if (unit === 'inches-lbs' && !mode) return 40;
    //   }
    //   if (unit === 'cms-kgs' && mode === 'weight') return 200;
    //   if (unit === 'inches-lbs' && mode === 'weight') return 440;

    //   if (unit === 'cms-kgs' && !mode) return 250;
    //   if (unit === 'inches-lbs' && !mode) return 100;
    // },
    maxValue: (unit, mode, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        if (unit === 'cms-kgs' && mode === 'weight') return 20;
        if (unit === 'inches-lbs' && mode === 'weight') return 50;

        if (unit === 'cms-kgs' && !mode) return 100;
        if (unit === 'inches-lbs' && !mode) return 40;
      }

      const checkMode = mode === 'weight' ? 'weight' : 'height';
      const bounds = getSliderBounds(unit, checkMode, ageGroup, dataResFloor, dataResCeil);
      return bounds.max;
    },
    // maxValue: (unit, mode, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 20;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 50;
    //     if (unit === 'cms-kgs' && !mode) return 100;
    //     if (unit === 'inches-lbs' && !mode) return 40;
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 15;
    //     if (unit === 'cms-kgs' && !mode) return 100;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 33;
    //     if (unit === 'inches-lbs' && !mode) return 40;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 50;
    //     if (unit === 'cms-kgs' && !mode) return 160;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 110;
    //     if (unit === 'inches-lbs' && !mode) return 60;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'cms-kgs' && mode === 'weight') return 200;
    //     if (unit === 'cms-kgs' && !mode) return 200;
    //     if (unit === 'inches-lbs' && mode === 'weight') return 440;
    //     if (unit === 'inches-lbs' && !mode) return 80;
    //   }
    // },
    isSingleInput: (_) => true,
    getUnitDisplay: (formData, field) => {
      const unit = formData.unit.split('-');
      return (
        <>
          (<span className="h_value">{field ? formData[field] : formData.l_measurement}</span>{' '}
          <strong>{field ? unit[1] : unit[0]}</strong>)
        </>
      );
    },
    getResultDisplay: (formData) => (
      <>
        <div className="d-flex justify-content-between my-md-2">
          <h6>Weight</h6>
          <span>
            {formData.w_measurement} {formData.unit.split('-')[1]}
          </span>
        </div>
        <div className="d-flex justify-content-between my-md-2">
          <h6>Height</h6>
          <span>
            {formData.l_measurement} {formData.unit.split('-')[0]}
          </span>
        </div>
        <div className="d-flex justify-content-between my-md-2">
          <h6>BMI</h6>
          <span>{formData.bmi}</span>
        </div>
      </>
    ),
  },
  calculateMeasurement: (formData, originalValue, isInitial, urlUint) => {
    let converted = {};
    const { unit } = formData;

    if (isInitial && urlUint === 'inches-lbs') {
      const l_measurement = _round(convertCmsToInches(formData.l_measurement));
      const w_measurement = _round(convertKgsToLbs(formData.w_measurement));
      converted = {
        unit: urlUint,
        l_measurement,
        bmi: _round(calculateAmericanBmi(w_measurement, l_measurement), 2),
        w_measurement,
        in_incs: l_measurement,
        in_lbs: w_measurement,
      };
    } else {
      if (unit === 'inches-lbs') {
        converted = {
          bmi: _round(calculateAmericanBmi(formData.w_measurement, formData.l_measurement), 2),
        };
      } else {
        converted = {
          bmi: _round(calculateMetricBmi(formData.w_measurement, formData.l_measurement), 2),
        };
      }
    }

    return converted;
  },
  handleMeasurement: (value, unit, previousUnit, isInitial, formData) => {
    let converted = {};

    if (unit === 'inches-lbs') {
      converted = {
        l_measurement: _round(convertCmsToInches(formData.l_measurement)),
        w_measurement: _round(convertKgsToLbs(formData.w_measurement)),
        in_incs: _round(convertCmsToInches(formData.l_measurement)),
        in_lbs: _round(convertKgsToLbs(formData.w_measurement)),
      };
    } else if (unit === 'cms-kgs') {
      converted = {
        l_measurement: _round(convertInchesToCms(formData.l_measurement)),
        w_measurement: _round(convertLbsToKgs(formData.w_measurement)),
        in_cms: _round(convertInchesToCms(formData.l_measurement)),
        in_kgs: _round(convertLbsToKgs(formData.w_measurement)),
      };
    } else if ('in-lbs-init' === unit) {
      converted = _round(calculateAmericanBmi(formData.w_measurement, formData.l_measurement), 2);
    }

    if ('cms-kgs-init' === unit) {
      converted = _round(calculateMetricBmi(formData.w_measurement, formData.l_measurement), 2);
    }
    return converted;
  },
  unitOptions: {
    first: {
      name: 'cms-kgs',
      label: 'cms / kgs',
    },
    second: {
      name: 'inches-lbs',
      label: 'inches / lbs',
    },
  },
};

export const netWorthConfig = {
  chartType: 'NetWorthChart',
  measurementType: 'net-worth',
  chartTitle: 'Net Worth',
  chartLinkNmae: 'net-worth',
  chartSubtitle: 'Net Worth for Age',
  measurementConfig: {
    stepValue: (unit, l_measurement) => {
      if (l_measurement < 10000) {
        return 500;
      } else if (l_measurement < 100000) {
        return 5000;
      } else if (l_measurement < 1000000) {
        return 50000;
      } else {
        return 1000000;
      }
    },
    // min: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (unit === 'kgs') return 2;
    //   else if (unit === 'lbs') return 4;
    //   else return 1;
    // },
    min: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.min(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.min;
    },
    // min: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 2.5;
    //     else if (unit === 'lbs') return 5.5;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 10;
    //     else if (unit === 'lbs') return 22;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 30;
    //     else if (unit === 'lbs') return 66;
    //   }
    // },

    // maxValue: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (unit === 'kgs') return 200;
    //   else if (unit === 'lbs') return 440;
    //   else return 200;
    // },
    maxValue: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.maxValue(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.max;
    },
    // maxValue: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 15;
    //     else if (unit === 'lbs') return 33;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 50;
    //     else if (unit === 'lbs') return 110;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 200;
    //     else if (unit === 'lbs') return 440;
    //   }
    // },

    isSingleInput: (unit) => true,
    getUnitDisplay: (formData) => {
      let value = formData.l_measurement;
      let unit = 'USD';
      if (formData.unit === 'USD') {
        unit = 'USD';
      }
      return (
        <>
          (<span className="h_value">{value}</span> <strong>{unit}</strong>)
        </>
      );
    },
    getResultDisplay: ({ in_kgs, in_lbs, unit, l_measurement }, t, locale) => (
      <div className="d-flex justify-content-between my-md-2">
        <h6>{t('chartType.netWorth')}</h6>
        <span>{formatCurrency(l_measurement, locale)}</span>
      </div>
    ),
  },
  calculateMeasurement: (formData, originalValue, isInitial, urlUnit) => {
    let converted = {};
    const { unit } = formData;
    if (isInitial) {
      converted = {
        l_measurement: _round(originalValue),
        // in_lbs: _round(convertKgsToLbs(originalValue)),
        // in_kgs: _round(originalValue),
      };
      if (urlUnit && urlUnit !== 'USD') {
        // converted = {
        //TODO Write currency convertion code here
        // ...weightConfig.handleMeasurement(converted.l_measurement, urlUnit),
        // unit: urlUnit,
        // };
      } else {
        // get unit by geo location
      }
    } else {
      if (unit === 'USD') {
        converted = {
          l_measurement: formData.l_measurement,
        };
      } else {
        converted = {
          l_measurement: formData.l_measurement,
        };
      }
    }
    return converted;
  },
  handleMeasurement: (value, unit, _, isInitial) => {
    let converted = {};

    if (unit === 'USD') {
      converted = {
        l_measurement: _round(value),
        // in_lbs: _round(convertKgsToLbs(value)),
        // in_kgs: _round(value),
      };
    } else if (unit === 'USD') {
      converted = {
        l_measurement: _round(value),
        // in_kgs: _round(convertLbsToKgs(value)),
        // in_lbs: _round(value),
      };
    }

    return converted;
  },
  unitOptions: {
    first: {
      name: 'USD',
      label: 'USD',
    },
    second: {
      name: 'USD',
      label: 'USD',
    },
  },
};
export const annualIncomeConfig = {
  chartType: 'AnnualIncomeChart',
  measurementType: 'annual-income',
  chartTitle: 'Annual Income',
  chartLinkNmae: 'annual-income',
  chartSubtitle: 'Annual Income for Age',
  measurementConfig: {
    stepValue: (unit, l_measurement) => {
      if (l_measurement < 10000) {
        return 500;
      } else if (l_measurement < 100000) {
        return 5000;
      } else if (l_measurement < 1000000) {
        return 50000;
      } else {
        return 1000000;
      }
    },
    // min: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (unit === 'kgs') return 2;
    //   else if (unit === 'lbs') return 4;
    //   else return 1;
    // },
    min: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.min(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.min;
    },
    // min: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 2.5;
    //     else if (unit === 'lbs') return 5.5;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 10;
    //     else if (unit === 'lbs') return 22;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 30;
    //     else if (unit === 'lbs') return 66;
    //   }
    // },

    // maxValue: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (unit === 'kgs') return 200;
    //   else if (unit === 'lbs') return 440;
    //   else return 200;
    // },
    maxValue: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.maxValue(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.max;
    },
    // maxValue: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 15;
    //     else if (unit === 'lbs') return 33;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 50;
    //     else if (unit === 'lbs') return 110;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 200;
    //     else if (unit === 'lbs') return 440;
    //   }
    // },

    isSingleInput: (unit) => true,
    getUnitDisplay: (formData) => {
      let value = formData.l_measurement;
      let unit = 'USD';
      if (formData.unit === 'USD') {
        unit = 'USD';
      }
      return (
        <>
          (<span className="h_value">{value}</span> <strong>{unit}</strong>)
        </>
      );
    },
    getResultDisplay: ({ in_kgs, in_lbs, unit, l_measurement }, t, locale) => (
      <div className="d-flex justify-content-between my-md-2">
        <h6>{t('chartType.annualIncome')}</h6>
        <span>{formatCurrency(l_measurement, locale)}</span>
      </div>
    ),
  },
  calculateMeasurement: (formData, originalValue, isInitial, urlUnit) => {
    let converted = {};
    const { unit } = formData;
    if (isInitial) {
      converted = {
        l_measurement: _round(originalValue),
        // in_lbs: _round(convertKgsToLbs(originalValue)),
        // in_kgs: _round(originalValue),
      };
      if (urlUnit && urlUnit !== 'USD') {
        // converted = {
        //TODO Write currency convertion code here
        // ...weightConfig.handleMeasurement(converted.l_measurement, urlUnit),
        // unit: urlUnit,
        // };
      } else {
        // get unit by geo location
      }
    } else {
      if (unit === 'USD') {
        converted = {
          l_measurement: formData.l_measurement,
        };
      } else {
        converted = {
          l_measurement: formData.l_measurement,
        };
      }
    }
    return converted;
  },
  handleMeasurement: (value, unit, _, isInitial) => {
    let converted = {};

    if (unit === 'USD') {
      converted = {
        l_measurement: _round(value),
        // in_lbs: _round(convertKgsToLbs(value)),
        // in_kgs: _round(value),
      };
    } else if (unit === 'USD') {
      converted = {
        l_measurement: _round(value),
        // in_kgs: _round(convertLbsToKgs(value)),
        // in_lbs: _round(value),
      };
    }

    return converted;
  },
  unitOptions: {
    first: {
      name: 'USD',
      label: 'USD',
    },
    second: {
      name: 'USD',
      label: 'USD',
    },
  },
};

export const retirementFundConfig = {
  chartType: 'RetirementFundChart',
  measurementType: 'retirement-fund',
  chartTitle: 'Retirement Fund',
  chartLinkNmae: 'retirement-fund',
  chartSubtitle: 'Retirement Fund for Age',
  measurementConfig: {
    stepValue: (unit, l_measurement) => {
      if (l_measurement < 10000) {
        return 500;
      } else if (l_measurement < 100000) {
        return 5000;
      } else if (l_measurement < 1000000) {
        return 50000;
      } else {
        return 1000000;
      }
    },
    // min: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (unit === 'kgs') return 2;
    //   else if (unit === 'lbs') return 4;
    //   else return 1;
    // },
    min: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.min(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.min;
    },
    // min: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.min(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 2.5;
    //     else if (unit === 'lbs') return 5.5;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 10;
    //     else if (unit === 'lbs') return 22;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 30;
    //     else if (unit === 'lbs') return 66;
    //   }
    // },

    // maxValue: (unit, isLengthChart) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (unit === 'kgs') return 200;
    //   else if (unit === 'lbs') return 440;
    //   else return 200;
    // },
    maxValue: (unit, isLengthChart, ageGroup, dataResFloor, dataResCeil) => {
      if (isLengthChart) {
        return lengthConfig.maxValue(unit);
      }
      const bounds = getSliderBounds(unit, false, ageGroup, dataResFloor, dataResCeil);
      return bounds.max;
    },
    // maxValue: (unit, isLengthChart, ageGroup) => {
    //   if (isLengthChart) {
    //     return lengthConfig.maxValue(unit);
    //   }
    //   if (getAgeGroup(ageGroup) === 'baby' || getAgeGroup(ageGroup) === 'toddler') {
    //     if (unit === 'kgs') return 15;
    //     else if (unit === 'lbs') return 33;
    //   }
    //   if (getAgeGroup(ageGroup) === 'preschooler' || getAgeGroup(ageGroup) === 'child') {
    //     if (unit === 'kgs') return 50;
    //     else if (unit === 'lbs') return 110;
    //   }
    //   if (getAgeGroup(ageGroup) === 'teenager' || getAgeGroup(ageGroup) === 'adult') {
    //     if (unit === 'kgs') return 200;
    //     else if (unit === 'lbs') return 440;
    //   }
    // },

    isSingleInput: (unit) => true,
    getUnitDisplay: (formData) => {
      let value = formData.l_measurement;
      let unit = 'USD';
      if (formData.unit === 'USD') {
        unit = 'USD';
      }
      return (
        <>
          (<span className="h_value">{value}</span> <strong>{unit}</strong>)
        </>
      );
    },
    getResultDisplay: ({ in_kgs, in_lbs, unit, l_measurement }, t, locale) => (
      <div className="d-flex justify-content-between my-md-2">
        <h6>{t('chartType.retirementFund')}</h6>
        <span>{formatCurrency(l_measurement, locale)}</span>
      </div>
    ),
  },
  calculateMeasurement: (formData, originalValue, isInitial, urlUnit) => {
    let converted = {};
    const { unit } = formData;
    if (isInitial) {
      converted = {
        l_measurement: _round(originalValue),
        // in_lbs: _round(convertKgsToLbs(originalValue)),
        // in_kgs: _round(originalValue),
      };
      if (urlUnit && urlUnit !== 'USD') {
        // converted = {
        //TODO Write currency convertion code here
        // ...weightConfig.handleMeasurement(converted.l_measurement, urlUnit),
        // unit: urlUnit,
        // };
      } else {
        // get unit by geo location
      }
    } else {
      if (unit === 'USD') {
        converted = {
          l_measurement: formData.l_measurement,
        };
      } else {
        converted = {
          l_measurement: formData.l_measurement,
        };
      }
    }
    return converted;
  },
  handleMeasurement: (value, unit, _, isInitial) => {
    let converted = {};

    if (unit === 'USD') {
      converted = {
        l_measurement: _round(value),
        // in_lbs: _round(convertKgsToLbs(value)),
        // in_kgs: _round(value),
      };
    } else if (unit === 'USD') {
      converted = {
        l_measurement: _round(value),
        // in_kgs: _round(convertLbsToKgs(value)),
        // in_lbs: _round(value),
      };
    }

    return converted;
  },
  unitOptions: {
    first: {
      name: 'USD',
      label: 'USD',
    },
    second: {
      name: 'USD',
      label: 'USD',
    },
  },
};
